/**
 * Die Musik-Karte — der Player bleibt in der App. (11.10.2026, Ticket 2937)
 *
 * Martin, 02.10.: *„Der Player ist beim Lied ‚Genug ist nicht genug‘ nach außen
 * gesprungen, statt eingebettet in Vespera zu laufen. Früher lief er
 * eingebettet und groß.“* Er hatte nichts angetippt.
 *
 * ## Woher der Absprung kam
 *
 * Neben dem eingebetteten Spieler rief `app.ts` bei JEDER live angeforderten
 * Musik zusätzlich `openExternalUrl(watchUrl ?? musicUrl)` auf (zwei Stellen:
 * Chat-Weg und Sprach-Weg). Am 04.07. wollte Martin das („am iPhone direkt
 * YouTube Music öffnen“); seit die App Adressen ohne Geste ans System gibt,
 * verlässt sie dabei wirklich die App. Der Spieler lief also doppelt: in der
 * Karte UND draußen.
 *
 * ## Was hier gilt
 *
 * - Die Karte öffnet NICHTS von selbst. Der Spieler läuft in ihr; wer nach
 *   draußen will, tippt „In YouTube öffnen“ (eigene Entscheidung, 44 pt hoch).
 * - Immer normales YouTube, nie YouTube Music: nur YouTube-Videos lassen sich
 *   einbetten, und eine `music.youtube.com`-Adresse nähme den Spieler aus der
 *   Karte. Die Adresse kommt aus der Video-Kennung, NICHT aus den Feldern
 *   `watchUrl`/`musicUrl`, die als Text in alten Nachrichten stehen (ein
 *   gespeicherter Text lässt sich nicht mehr berichtigen, siehe
 *   shared/video-spieler.ts).
 * - Die Spieler-Adresse wird beim Zeichnen gebaut: Vollbild (`fs=1`) und
 *   `playsinline=1` (iOS: kein Sprung in den nativen Vollbildspieler beim
 *   Start) gelten so auch für eine Karte von gestern.
 * - Autoplay nur, wenn die Musik JETZT angefordert wurde; der Verlauf bleibt
 *   stumm (Geistermusik, 09.07.).
 *
 * ⚠️ Host bleibt `www.youtube.com`, NICHT `youtube-nocookie.com` wie bei den
 *    Fokus-Videos: dort schützt der Datenschutz-Modus vor einem Köder für
 *    später, hier ist es Musik, und das Konto soll den Spieler kennen.
 */
import { SPIELER_PARAMETER, videoKennung } from "../../shared/video-spieler.js";
import { beobachteSpieler } from "./wiedergabe-stand.js";

export interface MusicResult {
  ok: boolean;
  videoId?: string;
  title?: string;
  channel?: string;
  thumbnail?: string | null;
  embedUrl?: string;
  /** Alt: als Text in gespeicherten Nachrichten. Wird nicht mehr gelesen. */
  watchUrl?: string;
  /** Alt (music.youtube.com): wird nicht mehr gelesen. */
  musicUrl?: string;
  query?: string;
  error?: string;
}

export function parseMusicResult(result: unknown): MusicResult | null {
  if (typeof result === "string") {
    try {
      return JSON.parse(result) as MusicResult;
    } catch {
      return null;
    }
  }
  if (result && typeof result === "object") return result as MusicResult;
  return null;
}

// 2026-07-05 (Martin): YouTube-Titel entruempeln — nur Klammer-Zusaetze mit
// Gruempel-Schluesselwoertern (Official Video, HD, Lyrics, 4K Remaster …)
// entfernen; echte Titelteile wie "(Turn Around)" bleiben stehen.
function cleanMusicTitle(raw: string): string {
  const cleaned = raw
    .replace(
      /\s*[(\[][^)\]]*\b(official|video|audio|lyric|lyrics|hd|hq|4k|8k|mv|visualizer|remaster(ed)?|explicit|clip|topic)\b[^)\]]*[)\]]/gi,
      "",
    )
    .replace(/\s{2,}/g, " ")
    .trim();
  return cleaned || raw.trim();
}

/** Die Video-Kennung der Karte: das Feld, sonst aus einer gespeicherten Einbett-Adresse. */
function kennungVon(data: MusicResult): string | null {
  return videoKennung(data.videoId ?? "") ?? videoKennung(data.embedUrl ?? "");
}

/** Die Adresse des eingebetteten Spielers — frisch gebaut, `autoplay` nur auf Wunsch. */
export function musikSpielerAdresse(kennung: string, autoplay: boolean): string {
  const p = new URLSearchParams(SPIELER_PARAMETER);
  if (autoplay) p.set("autoplay", "1");
  // Immer: der Player meldet Spielt/Pause an die Seite (lib/wiedergabe-stand.ts) - Zugzeile, Mikro nach der Musik, Hinweis (3232/3230/3231).
  p.set("enablejsapi", "1");
  return `https://www.youtube.com/embed/${kennung}?${p}`;
}

/** Der Weg nach draußen, wenn Martin ihn selbst wählt: das Video auf normalem YouTube. Kein Mix, kein YouTube Music. */
export function musikLink(kennung: string): string {
  return `https://www.youtube.com/watch?v=${kennung}`;
}

/**
 * Die Karte zu einem Ergebnis von play_music. Öffnet nichts von selbst und
 * hängt sich nicht in den Chat — das tut der Aufrufer.
 */
export function baueMusikKarte(data: MusicResult, opts?: { autoplay?: boolean }): HTMLElement {
  const node = document.createElement("div");
  node.className = "bubble music bot";
  if (data.videoId) node.dataset.musicId = data.videoId;
  const kennung = kennungVon(data);
  if (!data.ok || !kennung) {
    node.textContent = data.error
      ? `Konnte das nicht abspielen: ${data.error}`
      : "Konnte den Song nicht finden.";
    return node;
  }
  const titelText = cleanMusicTitle(data.title ?? "Unbekannter Track");

  const head = document.createElement("div");
  head.className = "music-head";
  if (data.thumbnail) {
    const thumb = document.createElement("img");
    thumb.className = "music-thumb";
    thumb.src = data.thumbnail;
    thumb.alt = data.title ?? "";
    thumb.loading = "lazy";
    head.appendChild(thumb);
  }
  const meta = document.createElement("div");
  meta.className = "music-meta";
  const title = document.createElement("div");
  title.className = "music-title";
  title.textContent = titelText;
  meta.appendChild(title);
  if (data.channel) {
    const ch = document.createElement("div");
    ch.className = "music-channel";
    ch.textContent = data.channel;
    meta.appendChild(ch);
  }
  head.appendChild(meta);
  node.appendChild(head);

  const iframe = document.createElement("iframe");
  iframe.className = "music-iframe";
  iframe.src = musikSpielerAdresse(kennung, opts?.autoplay === true);
  iframe.title = `Player: ${titelText}`;
  /*
   * `allowfullscreen` UND `fullscreen` in `allow`: die Berechtigungs-Richtlinie
   * ist die neuere Form, das alte Attribut brauchen ältere WebKit-Fassungen.
   * Ohne beides bleibt der Vollbild-Knopf im Spieler wirkungslos.
   * ⚠️ KEIN sandbox: es würde Titel- und Logo-Klicks im Spieler abschneiden,
   * und das untersagen die YouTube-Bedingungen.
   */
  iframe.allow =
    "accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen; gyroscope; picture-in-picture";
  iframe.setAttribute("allowfullscreen", "");
  iframe.referrerPolicy = "strict-origin-when-cross-origin";
  node.appendChild(iframe);
  if (opts?.autoplay === true) {
    const hinweis = document.createElement("p");
    hinweis.className = "music-hint";
    hinweis.setAttribute("role", "status");
    hinweis.textContent = "Startet nichts? Tippe ▶ im Player – iOS erlaubt den Start ohne Tippen nicht immer.";
    node.appendChild(hinweis);
    // Ticket 3231: der Hinweis geht, sobald der gemeinsame Hörer "spielt" meldet.
    beobachteSpieler(iframe, () => hinweis.remove());
  } else {
    beobachteSpieler(iframe);
  }

  const a = document.createElement("a");
  a.className = "music-open-link";
  a.href = musikLink(kennung);
  a.target = "_blank";
  a.rel = "noopener";
  a.textContent = "In YouTube öffnen";
  node.appendChild(a);
  return node;
}
